import { DEVICE_AUTH_INACTIVITY_MINUTES } from '@/common/env'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import {
    clearStoredPasskeyIfUserMismatch,
    getPasskeySetupSkipped,
    hasStoredPasskey,
    RegisterPasskeyOptions,
    registerPasskey,
    runDeviceAuth as runPasskeyAuth,
    setPasskeySetupSkipped,
} from './webauthn'
import {
    persistDeviceAuthFailureAttempt,
    persistDeviceAuthSignedOut,
    persistDeviceAuthSuccess,
    resolveDeviceAuthRequirement,
} from './deviceAuth/adapter'
import { DeviceAuthEvent, initialDeviceAuthState, reduceDeviceAuthState } from './deviceAuth/machine'

type SetupChoice = 'setup' | 'skip'
type AuthChoice = 'use-passkey' | 'logout'

type DeviceAuthReason = 'appAuthenticated' | 'inactivityExpired' | 'manual'

type PendingChoiceResolver =
    | { kind: 'setup'; resolve: (choice: SetupChoice) => void }
    | { kind: 'auth'; resolve: (choice: AuthChoice) => void }

export function useDeviceAuthFlow(props: {
    userId?: string
    onManualSignOut: () => Promise<void>
    getRegisterPasskeyOptions: () => RegisterPasskeyOptions | null
}) {
    const { userId, onManualSignOut, getRegisterPasskeyOptions } = props

    const [machineState, machineDispatch] = useReducer(reduceDeviceAuthState, initialDeviceAuthState)
    const machineStateRef = useRef(machineState)
    const pendingChoiceRef = useRef<PendingChoiceResolver | null>(null)
    const requestDeviceAuthPromise = useRef<Promise<boolean> | null>(null)

    useEffect(() => {
        machineStateRef.current = machineState
    }, [machineState])

    const dispatchMachine = useCallback(
        (event: DeviceAuthEvent) => {
            machineDispatch(event)
        },
        [machineDispatch],
    )

    useEffect(() => {
        return () => {
            // Do not automatically treat an unresolved setup prompt as a user "skip"
            // when the component unmounts (for example on refresh). Only resolve
            // an in-flight auth prompt to 'logout' so callers don't remain blocked.
            if (pendingChoiceRef.current?.kind === 'auth') {
                pendingChoiceRef.current.resolve('logout')
            }
            // Clear any pending resolver without forcing a choice for setup.
            pendingChoiceRef.current = null
            // If there was an in-flight request promise, clear it so remounts
            // (React StrictMode double-mount) don't reuse a stale promise tied
            // to the previous component instance.
            requestDeviceAuthPromise.current = null
            dispatchMachine({ type: 'reset' })
        }
    }, [dispatchMachine])

    const requestPasskeySetupChoice = useCallback(
        async (error?: string | null) => {
            dispatchMachine({ type: 'promptSetup', error })
            // Yield to the event loop so React can flush the UI update and mount the modal
            // before we await the user's choice. This avoids races where the prompt is
            // dispatched but a re-render/unmount hides it before the promise is awaited.
            await new Promise((r) => setTimeout(r, 0))
            return new Promise<SetupChoice>((resolve) => {
                pendingChoiceRef.current = { kind: 'setup', resolve }
            })
        },
        [dispatchMachine],
    )

    const resolvePasskeySetupChoice = useCallback((choice: SetupChoice) => {
        if (pendingChoiceRef.current?.kind !== 'setup') return
        const resolver = pendingChoiceRef.current
        pendingChoiceRef.current = null
        resolver.resolve(choice)
    }, [])

    const requestPasskeyAuthChoice = useCallback(
        async (error?: string | null) => {
            dispatchMachine({ type: 'promptAuth', error })
            await new Promise((r) => setTimeout(r, 0))
            return new Promise<AuthChoice>((resolve) => {
                pendingChoiceRef.current = { kind: 'auth', resolve }
            })
        },
        [dispatchMachine],
    )

    const resolvePasskeyAuthChoice = useCallback((choice: AuthChoice) => {
        if (pendingChoiceRef.current?.kind !== 'auth') return
        const resolver = pendingChoiceRef.current
        pendingChoiceRef.current = null
        resolver.resolve(choice)
    }, [])

    const requestDeviceAuth = useCallback(
        async (reason: DeviceAuthReason = 'manual'): Promise<boolean> => {
            const current = machineStateRef.current
            if (current.status === 'verified') {
                return true
            }
            if (requestDeviceAuthPromise.current) {
                return requestDeviceAuthPromise.current
            }
            if (current.status === 'prompting' || current.status === 'verifying') {
                return false
            }

            const body = async (): Promise<boolean> => {
                try {
                    dispatchMachine({ type: reason === 'inactivityExpired' ? 'inactivityExpired' : 'appAuthenticated' })

                    const requirement = resolveDeviceAuthRequirement(+DEVICE_AUTH_INACTIVITY_MINUTES * 60 * 1000)
                    if (!requirement.shouldPrompt) {
                        dispatchMachine({ type: 'passkeySuccess' })
                        return true
                    }

                    if (!userId) {
                        dispatchMachine({ type: 'passkeyFailure', error: 'Missing user identity for device auth.' })
                        return false
                    }

                    clearStoredPasskeyIfUserMismatch(userId)

                    const hasPasskey = hasStoredPasskey()

                    if (!hasPasskey) {
                        if (getPasskeySetupSkipped()) {
                            persistDeviceAuthSuccess()
                            dispatchMachine({ type: 'passkeySuccess' })
                            return true
                        }

                        const initialRegisterPasskeyOptions = getRegisterPasskeyOptions()
                        if (!initialRegisterPasskeyOptions) {
                            dispatchMachine({
                                type: 'passkeyFailure',
                                error: 'Registration details unavailable for passkey setup.',
                            })
                            return false
                        }

                        let setupError: string | null = null
                        let awaitingSetupDecision = true
                        while (awaitingSetupDecision) {
                            const choice = await requestPasskeySetupChoice(setupError)
                            if (choice === 'skip') {
                                setPasskeySetupSkipped(true)
                                persistDeviceAuthSuccess()
                                dispatchMachine({ type: 'passkeySuccess' })
                                return true
                            }

                            dispatchMachine({ type: 'userChosePasskey' })
                            const registerPasskeyOptions = getRegisterPasskeyOptions()
                            if (!registerPasskeyOptions) {
                                dispatchMachine({
                                    type: 'passkeyFailure',
                                    error: 'Registration details unavailable for passkey setup.',
                                })
                                return false
                            }

                            const created = await registerPasskey(registerPasskeyOptions)
                            if (created) {
                                setPasskeySetupSkipped(false)
                                persistDeviceAuthSuccess()
                                dispatchMachine({ type: 'passkeySuccess' })
                                awaitingSetupDecision = false
                                return true
                            }

                            setupError = 'Could not set up passkey. Try again or skip for now.'
                            dispatchMachine({ type: 'passkeyFailure', error: setupError })
                        }
                    }

                    let authError: string | null = null
                    let awaitingAuthDecision = true
                    while (awaitingAuthDecision) {
                        const choice = await requestPasskeyAuthChoice(authError)
                        if (choice === 'logout') {
                            persistDeviceAuthSignedOut()
                            await onManualSignOut()
                            return false
                        }

                        dispatchMachine({ type: 'userChosePasskey' })
                        const ok = await runPasskeyAuth()
                        if (ok) {
                            persistDeviceAuthSuccess()
                            dispatchMachine({ type: 'passkeySuccess' })
                            awaitingAuthDecision = false
                            return true
                        }

                        authError = 'Passkey authentication failed. Try again or log out.'
                        persistDeviceAuthFailureAttempt()
                        dispatchMachine({ type: 'passkeyFailure', error: authError })
                    }

                    return false
                } catch {
                    persistDeviceAuthFailureAttempt()
                    dispatchMachine({ type: 'passkeyFailure', error: 'Unexpected device auth error.' })
                    return false
                }
            }

            requestDeviceAuthPromise.current = body()
            try {
                const result = await requestDeviceAuthPromise.current
                return result
            } finally {
                requestDeviceAuthPromise.current = null
            }
        },
        [
            getRegisterPasskeyOptions,
            onManualSignOut,
            requestPasskeyAuthChoice,
            requestPasskeySetupChoice,
            userId,
            dispatchMachine,
        ],
    )

    // Keep the setup modal open while we're prompting and also while
    // we're verifying (native passkey flow active). This prevents the
    // dialog from disappearing when `userChosePasskey` transitions the
    // state to 'verifying' while the native prompt runs.
    const passkeySetupModalOpen =
        (machineState.status === 'prompting' || machineState.status === 'verifying') &&
        machineState.promptKind === 'setup'
    const passkeyAuthModalOpen =
        machineState.promptKind === 'auth' &&
        (machineState.status === 'prompting' || machineState.status === 'verifying')
    const passkeySetupError = machineState.promptKind === 'setup' ? machineState.error : null
    const passkeyAuthError = machineState.promptKind === 'auth' ? machineState.error : null
    const passkeyAuthWaiting = machineState.status === 'verifying' && machineState.promptKind === 'auth'

    return {
        requestDeviceAuth,
        passkeySetupModalOpen,
        passkeySetupError,
        passkeyAuthModalOpen,
        passkeyAuthError,
        resolvePasskeySetupChoice,
        resolvePasskeyAuthChoice,
        passkeyAuthWaiting,
    }
}
